import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiFileText, FiFolder, FiUsers, FiMessageCircle } from 'react-icons/fi';
import * as searchService from '../../services/search';
import '../../styles/main.scss';

const Search = () => {
  const [query, setQuery] = useState('');
  const [types, setTypes] = useState({
    documents: true,
    categories: true,
    folders: true,
    users: false,
    comments: false,
  });

  const selectedTypes = Object.entries(types)
    .filter(([, value]) => value)
    .map(([key]) => key)
    .join(',');

  const { data, isLoading, refetch } = useQuery(
    ['global-search', query, selectedTypes],
    () =>
      searchService.globalSearch({
        q: query,
        types: selectedTypes,
        limit: 10,
      }),
    {
      enabled: false,
    }
  );

  const results = data?.data?.results || {};

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!query.trim()) {
      return;
    }
    refetch();
  };

  const handleToggleType = (key) => {
    setTypes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="dashboard-page search-page">
      <div className="page-header">
        <div>
          <h1>Advanced Search</h1>
          <p>Search across documents, folders, categories and more</p>
        </div>
      </div>

      <div className="page-content">
        <motion.div
          className="section-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <form onSubmit={handleSubmit} className="search-form">
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Search documents, folders, categories..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className="search-types">
              <label>
                <input
                  type="checkbox"
                  checked={types.documents}
                  onChange={() => handleToggleType('documents')}
                />
                <span>Documents</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={types.categories}
                  onChange={() => handleToggleType('categories')}
                />
                <span>Categories</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={types.folders}
                  onChange={() => handleToggleType('folders')}
                />
                <span>Folders</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={types.users}
                  onChange={() => handleToggleType('users')}
                />
                <span>Users</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={types.comments}
                  onChange={() => handleToggleType('comments')}
                />
                <span>Comments</span>
              </label>
            </div>
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || !query.trim()}
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>
        </motion.div>

        {data && (
          <div className="search-results">
            {Object.keys(results).length === 0 && !isLoading ? (
              <div className="empty-state">
                <p>No results found for "{query}".</p>
              </div>
            ) : (
              <>
                {Array.isArray(results.documents) && results.documents.length > 0 && (
                  <motion.div
                    className="section-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="section-header">
                      <h2>Documents</h2>
                    </div>
                    <div className="search-section-list">
                      {results.documents.map((doc) => (
                        <Link
                          key={doc._id}
                          to={`/documents/${doc._id}`}
                          className="search-result-item"
                        >
                          <FiFileText />
                          <div className="search-result-content">
                            <div className="search-result-title">{doc.name}</div>
                            <div className="search-result-meta">
                              {doc.documentNumber && (
                                <span>{doc.documentNumber}</span>
                              )}
                              {doc.categoryId?.name && (
                                <span>· {doc.categoryId.name}</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}

                {Array.isArray(results.folders) && results.folders.length > 0 && (
                  <motion.div
                    className="section-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="section-header">
                      <h2>Folders</h2>
                    </div>
                    <div className="search-section-list">
                      {results.folders.map((folder) => (
                        <Link
                          key={folder._id}
                          to={`/folders/${folder._id}`}
                          className="search-result-item"
                        >
                          <FiFolder />
                          <div className="search-result-content">
                            <div className="search-result-title">{folder.name}</div>
                            <div className="search-result-meta">
                              {folder.categoryId?.name && (
                                <span>{folder.categoryId.name}</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}

                {Array.isArray(results.categories) && results.categories.length > 0 && (
                  <motion.div
                    className="section-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="section-header">
                      <h2>Categories</h2>
                    </div>
                    <div className="search-section-list">
                      {results.categories.map((category) => (
                        <Link
                          key={category._id}
                          to={`/categories/${category._id}`}
                          className="search-result-item"
                        >
                          <FiFolder />
                          <div className="search-result-content">
                            <div className="search-result-title">{category.name}</div>
                            <div className="search-result-meta">
                              <span>{category.description || 'Category'}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}

                {Array.isArray(results.users) && results.users.length > 0 && (
                  <motion.div
                    className="section-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="section-header">
                      <h2>Users</h2>
                    </div>
                    <div className="search-section-list">
                      {results.users.map((user) => (
                        <div key={user._id} className="search-result-item">
                          <FiUsers />
                          <div className="search-result-content">
                            <div className="search-result-title">{user.name}</div>
                            <div className="search-result-meta">
                              <span>{user.email}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {Array.isArray(results.comments) && results.comments.length > 0 && (
                  <motion.div
                    className="section-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="section-header">
                      <h2>Comments</h2>
                    </div>
                    <div className="search-section-list">
                      {results.comments.map((comment) => (
                        <Link
                          key={comment._id}
                          to={`/documents/${comment.documentId}`}
                          className="search-result-item"
                        >
                          <FiMessageCircle />
                          <div className="search-result-content">
                            <div className="search-result-title">
                              {comment.document?.name || 'Comment'}
                            </div>
                            <div className="search-result-meta">
                              <span>{comment.content}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
